import { useEffect, useState } from 'react';
import {
  useEmailStatus, useSaveEmailSender, useSendTestEmail,
} from '../../lib/api/hooks-payments.js';
import { useSession } from '../../lib/auth/session.js';
import { ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { ApiClientError } from '../../lib/api/client.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { formatDateTime } from '../../lib/format.js';

/**
 * The Resend account email goes out through (docs/28 B1): ArcNet's own on the
 * Billing setup page, and each reseller's on its record. A client's email goes
 * through its reseller's sender when that is switched on, and ArcNet's otherwise.
 *
 * The key is write-only. Saving asks for the person's password again, as the
 * PayFast merchant does: whoever holds the sender holds the name every receipt
 * goes out in.
 */

const banner = (e: unknown): e is ApiClientError =>
  e instanceof ApiClientError && e.code !== 'auth.step_up_required'
  && Object.keys(e.fieldErrors).length === 0;

export function EmailSenderCard({ resellerId }: { resellerId: string | null }) {
  const can = useSession((s) => s.can);
  const status = useEmailStatus(resellerId);
  const save = useSaveEmailSender(resellerId);
  const test = useSendTestEmail(resellerId);
  const saveStep = useStepUp();
  const testStep = useStepUp();

  const s = status.data ?? null;
  const sender = s?.sender ?? null;
  const writable = can('billing:write');
  const idp = `sender-${resellerId ?? 'platform'}`;

  const [from, setFrom] = useState('');
  const [key, setKey] = useState('');
  const [enabled, setEnabled] = useState(false);

  // A save comes back as the saved state. The key is never in it, so its box
  // goes back to empty.
  useEffect(() => {
    setFrom(sender?.from ?? '');
    setEnabled(sender?.enabled ?? false);
    setKey('');
  }, [sender]);

  const fieldErrors = save.error instanceof ApiClientError ? save.error.fieldErrors : {};

  const submit = () => void saveStep.run(() => save.mutateAsync({
    from: from.trim(),
    enabled,
    ...(key.trim() ? { api_key: key.trim() } : {}),
  }));

  return (
    <div className="arc-card" style={{ marginTop: 14 }}>
      <div className="arc-card-head">
        <h2 className="arc-h2">Email</h2>
        {s && (sender?.enabled
          ? <Badge tone="ok">sending</Badge>
          : s.fallback === 'platform'
            ? <Badge tone="muted">using the platform&apos;s</Badge>
            : <Badge tone="warn">not sending</Badge>)}
      </div>
      <div className="arc-card-body">
        {status.error ? <ErrorBanner error={status.error} /> : !s ? (
          <div className="arc-skeleton" style={{ height: 120 }} />
        ) : (
          <>
            <StepUpPrompt step={saveStep} what="Changing who email is sent as" />
            <StepUpPrompt step={testStep} what="Sending a test email" />
            {banner(save.error) && <ErrorBanner error={save.error} />}
            {banner(test.error) && <ErrorBanner error={test.error} />}

            <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
              <div className="arc-grid arc-grid-2">
                <div className="arc-field" style={{ margin: 0 }}>
                  <label className="arc-label" htmlFor={`${idp}-from`}>From</label>
                  <input id={`${idp}-from`} className="arc-input" value={from}
                    onChange={(e) => setFrom(e.target.value)} placeholder="Billing <billing@example.com>"
                    required disabled={!writable} aria-invalid={!!fieldErrors['from']} />
                  {fieldErrors['from']
                    ? <div className="arc-error">{fieldErrors['from']}</div>
                    : <div className="arc-hint">An address on a domain verified in Resend.</div>}
                </div>
                <div className="arc-field" style={{ margin: 0 }}>
                  <label className="arc-label" htmlFor={`${idp}-key`}>Resend API key</label>
                  <input id={`${idp}-key`} className="arc-input arc-mono" type="password"
                    autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)}
                    placeholder={sender ? 'Saved. Type a new one to replace it' : 're_…'}
                    required={!sender} disabled={!writable} aria-invalid={!!fieldErrors['api_key']} />
                  {fieldErrors['api_key']
                    ? <div className="arc-error">{fieldErrors['api_key']}</div>
                    : <div className="arc-hint">A sending-only key.</div>}
                </div>
              </div>

              <label className="arc-row" style={{ gap: 7, margin: '14px 0' }}>
                <input type="checkbox" checked={enabled} disabled={!writable}
                  onChange={(e) => setEnabled(e.target.checked)} />
                <span>Send email through this account</span>
              </label>

              {writable && (
                <div className="arc-row" style={{ gap: 10, flexWrap: 'wrap' }}>
                  <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending}>
                    {save.isPending ? 'Saving…' : 'Save'}
                  </button>
                  {sender && (
                    <button type="button" className="arc-btn" disabled={test.isPending}
                      onClick={() => void testStep.run(() => test.mutateAsync())}>
                      {test.isPending ? 'Sending…' : 'Send a test to me'}
                    </button>
                  )}
                  {test.data && (test.data.status === 'sent'
                    ? <span className="arc-muted">Sent to {test.data.to}</span>
                    : (
                      <span style={{ color: 'var(--arc-danger)' }}>
                        {test.data.error ?? `Not sent yet (${test.data.status})`}
                      </span>
                    ))}
                </div>
              )}
            </form>

            <dl className="arc-kv" style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--arc-border)' }}>
              {resellerId && !sender?.enabled && (
                <>
                  <dt>Without it</dt>
                  <dd>
                    {s.fallback === 'platform'
                      ? 'This reseller’s clients get email from the platform’s sender.'
                      : 'Nothing is sent: the platform has no sender switched on either.'}
                  </dd>
                </>
              )}
              <dt>Last test</dt>
              <dd>
                {sender?.last_tested_at
                  ? formatDateTime(sender.last_tested_at, 'Africa/Johannesburg')
                  : 'Not tested'}
              </dd>
              <dt>{resellerId ? 'Its clients, last 24 hours' : 'All email, last 24 hours'}</dt>
              <dd>{s.last_day.sent} sent, {s.last_day.waiting} waiting, {s.last_day.failed} failed</dd>
              {s.last_failure && (
                <>
                  <dt>Last error</dt>
                  <dd className="arc-mono" style={{ fontSize: 12 }}>{s.last_failure.error}</dd>
                </>
              )}
            </dl>
          </>
        )}
      </div>
    </div>
  );
}
